import { useActualIncidentId } from './commonHooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { useAppSelector, selectUserGuid } from 'slices/commonSelectors';
import type { IapAttachmentManifest } from 'components/IAP/iapAttachmentManifest';

type Agenda = {
  [phaseIndex: number]: { notes?: string[]; checked?: string[] };
};

export type IncidentInfo = {
  id: UUID;
  incidentId: UUID;
  version: number;
  timestamp: Date;
  userGuid: UUID;
  situationSummary: string;
  priorities: string;
  keyDecisionsAndProcedures: string;
  limitationsAndConstraints: string;
  criticalInformationRequirements: string;
  communicationsSpecialInstructions: string;
  mapSketchUrl: string;
  safetyPlanUrl: string;
  operationalPeriodCommandEmphasis: string;
  /** FEMA 202 box 5. Not printed on the Coast Guard 202. */
  situationalAwareness?: string;
  specialMedicalEmergencyProcedures: string;
  medicalAirAssetsYn: boolean;
  incidentLocation: string;
  communityLifelines?: string[];
  siteSafetyPlan?: boolean;
  siteSafetyPlanLocation?: string;
  agendaItems?: Agenda;
  nextOperationalPeriodStartDateTime?: Date | null;
  nextOperationalPeriodEndDateTime?: Date | null;
  /** Which forms and attachments belong in this period's IAP. */
  iapAttachmentManifest?: IapAttachmentManifest | null;
  /** File Manager id of the org-chart PNG captured when the ICS-207 was signed. */
  ics207OrgChartFileId?: UUID | null;
};

export const keys = {
  base: 'incident_info',
  get all() {
    return [keys.base];
  },
  get: (incidentId: UUID) => [keys.base, incidentId],
};

export const useIncidentInfo = (enabled: boolean = true) => {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: keys.get(incidentId),
    queryFn: async () => {
      const resp = await axios.get<IncidentInfo>(
        `${config.apiGateway.ics_incident_info}/${incidentId}`
      );
      return resp.data;
    },
    enabled,
  });
};

type EditIncidentInfoReqData = Partial<
  Omit<IncidentInfo, 'id' | 'incidentId' | 'userGuid' | 'version' | 'timestamp'>
>;

export const useUpdateIcsInfo = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useAppSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditIncidentInfoReqData) => {
      return await axios.put(
        `${config.apiGateway.ics_incident_info}/${incidentId}`,
        { ...data, userGuid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(incidentId),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
